import { useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import SectionHeading from "@/components/feature/SectionHeading";
import ShortFilmCard from "@/components/feature/ShortFilmCard";
import NewsListItem from "@/components/feature/NewsListItem";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import NewsFlashPanel from "@/pages/home/components/NewsFlashPanel";
import CategoryEntries from "@/pages/home/components/CategoryEntries";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { newsList } from "@/mocks/news";
import { shortFilms } from "@/mocks/shortFilms";
import { newsSortLatest, sortFilms } from "@/lib/content";
import { siteHighlights } from "@/mocks/site";
import { formatViews } from "@/utils/format";

export default function Home() {
  useDocumentMeta(
    "小电影 - 最新影视短片推荐与影视资讯平台",
    "小电影网站提供丰富的影视短片资源和最新影视资讯，涵盖短视频推荐、影视动态等内容，满足用户多样化的观影需求。",
    "小电影,影视短片,电影推荐,影视资讯,短视频,影视动态,影视平台",
  );

  const latestFilms = sortFilms(shortFilms, "latest");
  const hotFilms = sortFilms(shortFilms, "popular").slice(0, 5);
  const latestNews = newsSortLatest(newsList);
  const [visibleCount, setVisibleCount] = useState(8);
  const [loading, setLoading] = useState(false);

  const handleLoadMore = () => {
    if (loading || visibleCount >= latestFilms.length) return;
    setLoading(true);
    window.setTimeout(() => {
      setVisibleCount((prev) => Math.min(prev + 4, latestFilms.length));
      setLoading(false);
    }, 420);
  };

  return (
    <PageShell>
      <section className="bg-background-100/70 border-b border-background-200">
        <div className="container-page py-5 md:py-7">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.22em] text-accent-600 uppercase">
                Today&apos;s Picks
              </p>
              <h1 className="mt-1.5 text-xl md:text-2xl font-extrabold tracking-tight text-foreground-950">
                今天，看一部好短片
              </h1>
            </div>
            <p className="text-xs md:text-sm text-foreground-500">
              精选 {shortFilms.length} 部短片 · {newsList.length} 条影视资讯 · 每日更新
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
            <div className="lg:col-span-2">
              <HeroCarousel />
            </div>
            <NewsFlashPanel />
          </div>
        </div>
      </section>

      <section className="container-page py-8 md:py-10">
        <SectionHeading
          kicker="Genres"
          title="按类型找到你的下一部短片"
          description="剧情、动画、纪录片、科幻、喜剧，五种情绪入口"
          linkTo="/category/shortfilms"
          linkLabel="全部短片"
        />
        <CategoryEntries />
      </section>

      <section className="container-page pb-10 md:pb-12">
        <SectionHeading
          kicker="Latest Shorts"
          title="最新影视短片"
          description="刚刚上线的高分短片，含导演、时长与发布时间"
          linkTo="/category/shortfilms"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {latestFilms.slice(0, visibleCount).map((film) => (
            <ShortFilmCard key={film.id} film={film} />
          ))}
        </div>

        <div className="mt-8 flex flex-col items-center gap-3">
          <p className="text-xs text-foreground-500">
            已展示 {Math.min(visibleCount, latestFilms.length)} / {latestFilms.length} 部短片
          </p>
          {visibleCount < latestFilms.length ? (
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loading}
              className="inline-flex items-center gap-2 h-10 px-6 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  正在加载
                </>
              ) : (
                <>
                  <i className="ri-add-line" />
                  加载更多短片
                </>
              )}
            </button>
          ) : (
            <Link
              to="/category/shortfilms"
              className="inline-flex items-center gap-2 h-10 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              去分类页按条件筛选
              <i className="ri-arrow-right-line" />
            </Link>
          )}
        </div>
      </section>

      <section className="bg-background-100/70 border-y border-background-200 py-9 md:py-11">
        <div className="container-page grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <SectionHeading
              kicker="Film News"
              title="最新影视资讯"
              description="行业新闻、影评与活动报道，按发布时间排序"
              linkTo="/category/news"
              linkLabel="更多资讯"
            />
            <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
              {latestNews.slice(0, 6).map((item) => (
                <NewsListItem key={item.id} item={item} />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950 mb-1">
                <i className="ri-fire-line text-accent-600" />
                本周热看
              </h2>
              <ul className="mt-3 divide-y divide-background-200">
                {hotFilms.map((film, filmIndex) => (
                  <li key={film.id}>
                    <Link
                      to={`/detail/shortfilm/${film.id}`}
                      className="group flex items-center gap-3 py-2.5 cursor-pointer"
                    >
                      <span
                        className={`w-5 text-center font-label text-sm font-extrabold ${
                          filmIndex < 3 ? "text-accent-600" : "text-foreground-400"
                        }`}
                      >
                        {String(filmIndex + 1).padStart(2, "0")}
                      </span>
                      <div className="w-14 h-10 rounded overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={film.cover}
                          alt={film.title}
                          title={`${film.title} - 小电影`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                          {film.title}
                        </p>
                        <p className="mt-0.5 text-[11px] text-foreground-500">
                          {formatViews(film.views)} 次观看 · {film.type}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-primary-500 p-5 text-background-50">
              <p className="text-[11px] tracking-[0.2em] uppercase text-primary-100">Collection</p>
              <h2 className="mt-2 text-lg font-extrabold leading-snug">
                二十分钟，也能看完一个人的一生
              </h2>
              <p className="mt-2 text-[13px] leading-relaxed text-primary-100">
                我们按情绪整理了本月片单，从告别到留下，再到想象。
              </p>
              <Link
                to="/detail/news/october-playlist"
                className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-50 text-primary-700 text-[13px] font-semibold hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看片单
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-page py-9 md:py-11">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {siteHighlights.map((item) => (
            <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-4">
              <p className="text-xs text-foreground-500">{item.label}</p>
              <p className="mt-1.5 font-label text-2xl font-extrabold text-foreground-950">
                {item.value}
                <span className="ml-1 text-xs font-medium text-foreground-400">{item.unit}</span>
              </p>
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-lg border border-background-200 bg-background-100 px-5 py-6 md:px-7 md:py-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-extrabold text-foreground-950">关于小电影</h2>
            <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed max-w-2xl">
              我们不做搬运，只做筛选。每天从海量短片里挑出值得二十分钟的作品，并把行业里真实发生的事写清楚。
            </p>
          </div>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-foreground-300 text-sm font-medium text-foreground-800 hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            了解我们
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}